import { motion } from "framer-motion";
import { ArrowRight, Play } from "lucide-react";
import { TRUST_BADGES } from "../../data/site";
// Right-column visual. Swap for `HeroDashboard` to restore the KPI panel.
import HeroFunnel from "./HeroFunnel";

const stagger = {
  hidden: { opacity: 0 },
  show: { opacity: 1, transition: { staggerChildren: 0.08, delayChildren: 0.1 } },
};
const item = {
  hidden: { opacity: 0, y: 16 },
  show: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } },
};
export default function Hero() {
  return (
    <section
      id="top"
      className="relative pt-28 md:pt-32 pb-20 md:pb-28 bg-white overflow-hidden"
      data-testid="hero-section"
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-grid-pattern opacity-70 pointer-events-none" />
      <div className="absolute right-0 top-0 w-2/3 h-2/3 bg-gradient-to-bl from-electric/10 via-transparent to-transparent pointer-events-none" />

      <div className="container relative grid lg:grid-cols-12 gap-10 items-center">
        {/* LEFT — Copy */}
        <motion.div
          variants={stagger} initial="hidden" animate="show"
          className="lg:col-span-7"
        >
          <motion.div
            variants={item}
            className="inline-flex items-center gap-2 border border-zinc-200 bg-white px-3 py-1.5 mb-6"
            data-testid="hero-eyebrow"
          >
            <span className="w-1.5 h-1.5 bg-electric animate-blink" />
            <span className="text-overline text-charcoal">
              Global App Marketing Agency · 100+ Apps Ranked · 15+ Markets
            </span>
          </motion.div>

          <motion.h1
            variants={item}
            className="font-display text-[2rem] sm:text-4xl md:text-5xl lg:text-[4.25rem] leading-[1.05] sm:leading-[1.02] font-extrabold text-charcoal text-balance"
            data-testid="hero-headline"
          >
            The{" "}
            <span className="relative inline-block">
              <span className="relative z-10">App Marketing Agency</span>
              <span className="absolute inset-x-0 bottom-1 h-3 bg-electric/30 -z-0" />
            </span>{" "}
            that takes apps from zero to&nbsp;category&nbsp;leader.
          </motion.h1>

          <motion.p
            variants={item}
            className="mt-6 text-lg md:text-xl text-zinc-600 max-w-2xl leading-relaxed"
            data-testid="hero-subheadline"
          >
            AppFillip is a full-stack app marketing agency for iOS &amp;
            Android. We rank, acquire, convert and retain — across the App
            Store, Google Play and every paid channel that moves installs.
            One team. Six growth levers. 100+ apps to #1 across 15+ global
            markets.
          </motion.p>

          {/* Service-pill strip */}
          <motion.div
            variants={item}
            className="mt-6 flex flex-wrap gap-1.5"
            data-testid="hero-services"
          >
            {[
              "ASO",
              "User Acquisition",
              "Influencer",
              "Retention",
              "Creative",
              "Analytics",
            ].map((s) => (
              <span
                key={s}
                className="inline-flex items-center text-[11px] font-bold uppercase tracking-widest text-zinc-700 border border-zinc-200 bg-white px-2.5 py-1 hover:border-electric hover:text-electric transition-colors"
              >
                {s}
              </span>
            ))}
          </motion.div>

          <motion.div
            variants={item}
            className="mt-8 flex flex-wrap items-center gap-3"
          >
            <a
              href="/free-aso-audit"
              data-testid="hero-cta-audit"
              aria-label="Get a free app marketing audit"
              className="inline-flex items-center gap-2 bg-electric text-white px-6 py-3.5 font-semibold hover:bg-electric-400 hover:-translate-y-0.5 transition-all shadow-[4px_4px_0_0_#2D2D2D]"
            >
              Get Free App Audit <ArrowRight className="w-4 h-4" />
            </a>
            <a
              href="/build-your-plan"
              data-testid="hero-cta-build-plan"
              aria-label="Build your custom app marketing plan"
              className="inline-flex items-center gap-2 bg-charcoal text-white px-6 py-3.5 font-semibold hover:bg-electric transition-colors"
            >
              Build Your Plan <ArrowRight className="w-4 h-4" />
            </a>
            <a
              href="#case-studies"
              data-testid="hero-cta-cases"
              aria-label="See AppFillip case studies"
              className="inline-flex items-center gap-2 bg-white border border-charcoal text-charcoal px-6 py-3.5 font-semibold hover:bg-charcoal hover:text-white transition-colors"
            >
              <Play className="w-4 h-4" /> See Results
            </a>
          </motion.div>

          {/* In-hero trust bar — featured client wordmarks */}
          <motion.div
            variants={item}
            className="mt-10 pt-6 border-t border-zinc-200"
            data-testid="hero-trust-bar"
            aria-label="Trusted by leading apps"
          >
            <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-zinc-500 mb-4">
              Trusted by founders at
            </p>
            <div className="flex flex-wrap items-center gap-x-7 gap-y-3 text-zinc-400">
              {[
                "Studdy AI",
                "Porter",
                "XM Trading",
                "WageTap",
                "Vimcal",
                "BabyBus",
              ].map((name) => (
                <span
                  key={name}
                  className="font-display text-base md:text-lg font-extrabold tracking-tight hover:text-charcoal transition-colors"
                >
                  {name}
                </span>
              ))}
              <a
                href="/case-studies"
                className="inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-widest text-electric hover:gap-2 transition-all"
              >
                + 100 more <ArrowRight className="w-3 h-3" />
              </a>
            </div>
          </motion.div>

          {/* Trust badges */}
          <motion.dl
            variants={item}
            className="mt-10 grid grid-cols-2 sm:grid-cols-4 border-t border-zinc-200"
            data-testid="hero-trust-badges"
          >
            {TRUST_BADGES.map((b, i) => (
              <div
                key={b.label}
                className={`py-5 ${i !== 0 ? "sm:border-l" : ""} ${i % 2 !== 0 ? "border-l" : ""} border-zinc-200 sm:pl-5 ${i % 2 !== 0 ? "pl-5" : ""}`}
              >
                <dt className="font-display text-2xl sm:text-3xl md:text-4xl font-extrabold text-charcoal tracking-tight">
                  {b.value}
                </dt>
                <dd className="mt-1 text-xs uppercase tracking-[0.18em] text-zinc-500">
                  {b.label}
                </dd>
              </div>
            ))}
          </motion.dl>
        </motion.div>

        {/* RIGHT — growth funnel illustration */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
          className="lg:col-span-5 relative"
        >
          <div className="relative overflow-hidden lg:overflow-visible pb-3 lg:pb-0 pr-3 lg:pr-0">
            <HeroFunnel />
          </div>
        </motion.div>
      </div>
    </section>
  );
}
